import React, { useEffect, useState } from "react";
import { Button, Heading, Image, Popover, Portal, Separator, SimpleGrid, Switch, Text } from "@chakra-ui/react";
import * as Cesium from "cesium";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";

import HoRoadMarking from "../../../icons/HoRoadMarking";
import { getUsernameAndUserIdFormat, matchKeyInText, showToast } from "../../../utilies/Utility";
import { startRoadMarkingFlow, loadIconShapeFromSvg, startRoadMarkingMoveMode } from "../../../utilies/roadMarking/Create";
import Type from "../../../utilies/Types";
import { useMapObjects } from "../../../context/MapObjectsContext";

function RoadMarking() {
  // state
  const viewerRef = useAppStore((s) => s.viewerRef);
  const { mapObjects, setMapObjects } = useMapObjects();
  const activeLayer = useAppStore((s) => s.activeLayer);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const userData = useAppStore((s) => s.userData);

  const roadMarkingMode = useAppStore((s) => s.roadMarkingMode);
  const setRoadMarkingMode = useAppStore((s) => s.setRoadMarkingMode);

  const roadMarkingMovingMode = useAppStore((s) => s.roadMarkingMovingMode);
  const setRoadMarkingMovingMode = useAppStore((s) => s.setRoadMarkingMovingMode);

  // setters
  const setCanHightlightEntity = useAppStore.getState().setCanHightlightEntity;

  const ROAD_MARKING_ICONS = [
    "/road-marking/straight.svg",
    "/road-marking/left-turn.svg",
    "/road-marking/right-turn.svg",
    "/road-marking/left-and-straight.svg",
    "/road-marking/right-and-straight.svg",
    "/road-marking/u-turn.svg",
    "/road-marking/bicycle.svg",
    "/road-marking/rec.svg",
  ];

  const [isShowRoadMarking, stIsShowRoadMarking] = useState(false);
  const [roadMarkingHandler, setRoadMarkingHandler] = useState(null);

  const destroyRoadMarkingHandler = React.useCallback(() => {
    if (!roadMarkingHandler) return;
    try {
      roadMarkingHandler.destroy();
    } catch (e) {}
    setRoadMarkingHandler(null);
  }, [roadMarkingHandler]);

  useEffect(() => {
    if (roadMarkingMovingMode || roadMarkingMode) {
      setCanHightlightEntity(false);
    } else {
      setCanHightlightEntity(true);
    }
  }, [roadMarkingMode, roadMarkingMovingMode, setCanHightlightEntity]);

  useEffect(() => {
    if (!roadMarkingMode && !roadMarkingMovingMode) {
      destroyRoadMarkingHandler();
    }
  }, [roadMarkingMode, roadMarkingMovingMode, destroyRoadMarkingHandler]);

  useEffect(() => {
    if (activeLayer) {
      let show = false;

      for (const key in mapObjects) {
        const obj = mapObjects[key];
        if (obj && obj.id == activeLayer && matchKeyInText(obj.file_name, "road_marking")) {
          show = true;
          break;
        }
      }

      stIsShowRoadMarking(show);
    } else {
      destroyRoadMarkingHandler();
      setRoadMarkingMovingMode(false);
      stIsShowRoadMarking(false);
      setRoadMarkingMode(false);
    }
  }, [activeLayer, mapObjects, destroyRoadMarkingHandler, setRoadMarkingMode, setRoadMarkingMovingMode]);

  useEffect(() => {
    return () => {
      destroyRoadMarkingHandler();
    };
  }, [destroyRoadMarkingHandler]);

  useEffect(() => {
    if (!roadMarkingMode && !roadMarkingMovingMode) return;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();

      destroyRoadMarkingHandler();
      setRoadMarkingMode(false);
      setRoadMarkingMovingMode(false);
      showToast("Road marking modes canceled.", Type.NOTIFICATION.INFO);
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [roadMarkingMode, roadMarkingMovingMode, destroyRoadMarkingHandler, setRoadMarkingMode, setRoadMarkingMovingMode]);

  if (!isShowRoadMarking) return null;

  const addFeatureToActiveDataSource = (feature) => {
    if (!feature) return;

    const targetIndex = mapObjects.findIndex((m) => m && m.id == activeLayer);
    if (targetIndex === -1) {
      console.warn("No active mapObject found for layer:", activeLayer);
      return;
    }

    const targetObj = mapObjects[targetIndex];
    const dataSource = targetObj?.dataSource;
    if (!dataSource) {
      console.warn("No dataSource on mapObject:", targetObj);
      return;
    }

    const entities = dataSource.entities.values;
    let templateEntity = null;
    let maxId = 0;

    for (let i = 0; i < entities.length; i++) {
      const e = entities[i];

      if (!templateEntity && e.polygon) {
        templateEntity = e;
      }

      if (e.properties && e.properties.id !== undefined) {
        let v = e.properties.id;
        if (v && typeof v.getValue === "function") {
          v = v.getValue(Cesium.JulianDate.now());
        }
        if (typeof v === "number" && v > maxId) {
          maxId = v;
        }
      }
    }

    const newId = maxId + 2;

    const coords = feature.geometry.coordinates[0] || [];
    if (coords.length < 4) return;

    let perimeter = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      const [lon1, lat1] = coords[i];
      const [lon2, lat2] = coords[i + 1];

      const c1 = Cesium.Cartographic.fromDegrees(lon1, lat1);
      const c2 = Cesium.Cartographic.fromDegrees(lon2, lat2);

      const geodesic = new Cesium.EllipsoidGeodesic(c1, c2);
      const d = geodesic.surfaceDistance;
      perimeter += d;
    }

    const flatDegrees = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const [lon, lat] = coords[i];
      flatDegrees.push(lon, lat);
    }

    const polygonProps = {
      hierarchy: Cesium.Cartesian3.fromDegreesArray(flatDegrees),
    };

    if (templateEntity && templateEntity.polygon) {
      const tp = templateEntity.polygon;
      polygonProps.material = tp.material;
      polygonProps.height = tp.height;
      polygonProps.extrudedHeight = tp.extrudedHeight;
      polygonProps.outline = tp.outline;
      polygonProps.outlineColor = tp.outlineColor;
      polygonProps.classificationType = tp.classificationType;
      polygonProps.zIndex = tp.zIndex;
    } else {
      polygonProps.material = Cesium.Color.YELLOW.withAlpha(0.7);
      polygonProps.outline = true;
      polygonProps.outlineColor = Cesium.Color.BLACK;
    }

    const creator = getUsernameAndUserIdFormat(userData);

    dataSource.entities.add({
      polygon: polygonProps,
      properties: {
        id: newId,
        color: "white",
        length: perimeter.toFixed(2),
        ...(creator ? { creator } : {}),
      },
    });

    setMapObjects((prev) => prev.map((o, idx) => (idx === targetIndex ? { ...o, is_edited: true } : o)));
  };

  const getActiveDataSource = () => {
    const targetIndex = mapObjects.findIndex((m) => m && m.id == activeLayer);
    if (targetIndex === -1) return null;
    const targetObj = mapObjects[targetIndex];
    return targetObj?.dataSource || null;
  };

  return (
    <Popover.Root positioning={{ placement: "left-start" }}>
      <Popover.Trigger asChild>
        <Button
          variant="solid"
          p={0}
          opacity={1}
          bg={roadMarkingMode || roadMarkingMovingMode ? "green" : "white"}
          color={roadMarkingMode || roadMarkingMovingMode ? "white" : "MenuText"}
          onDoubleClick={() => {
            destroyRoadMarkingHandler();
            setRoadMarkingMode(false);
            setRoadMarkingMovingMode(false);
            showToast("Road marking modes canceled.", Type.NOTIFICATION.INFO);
          }}
        >
          <Tooltip content="Road Marking">
            <HoRoadMarking />
          </Tooltip>
        </Button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content width="fit-content">
            <Popover.Arrow />
            <Popover.Body p={2}>
              <Switch.Root
                checked={roadMarkingMovingMode}
                onChange={(v) => {
                  const checked = v.target.checked;

                  const viewer = viewerRef.current;
                  if (!viewer) return;

                  if (checked && roadMarkingMode) {
                    destroyRoadMarkingHandler();
                    setRoadMarkingMode(false);
                  }

                  if (!checked) {
                    destroyRoadMarkingHandler();
                    setRoadMarkingMovingMode(false);
                    showToast("Road marking moving disabled.", Type.NOTIFICATION.INFO);
                    return;
                  }

                  const dataSource = getActiveDataSource();
                  if (!dataSource) {
                    showToast("No active road marking layer to move.", Type.NOTIFICATION.WARNING);
                    return;
                  }

                  const handler = startRoadMarkingMoveMode(
                    viewer,
                    dataSource,
                    originalCameraControls,
                    () => {
                      setMapObjects((prev) => prev.map((m) => (m && m.id === activeLayer ? { ...m, is_edited: true } : m)));
                    },
                    userData,
                  );

                  setRoadMarkingHandler(handler);
                  setRoadMarkingMovingMode(true);
                  showToast("Click and drag a road marking to move it.", Type.NOTIFICATION.INFO);
                }}
              >
                <Switch.HiddenInput />
                <Switch.Control />
                <Switch.Label>Activate Moving</Switch.Label>
              </Switch.Root>

              <Separator mt={3} mb={2} />

              <Heading fontSize={"xl"} lineHeight={"20px"}>
                Icons:
              </Heading>
              <Text>Select one of the icons to draw</Text>

              <SimpleGrid columns={5} gap={2} p={2}>
                {ROAD_MARKING_ICONS.map((icon, index) => (
                  <Image
                    key={index}
                    src={icon}
                    width="30px"
                    cursor="pointer"
                    border="1px solid #ccc"
                    borderRadius="4px"
                    padding="2px"
                    onClick={async () => {
                      if (roadMarkingMode || roadMarkingMovingMode) return;

                      showToast("Click the start point and then the end point on the map.", Type.NOTIFICATION.INFO);

                      const viewer = viewerRef.current;
                      if (!viewer) return;

                      const shape = await loadIconShapeFromSvg(icon);
                      if (!shape) return;

                      const handler = startRoadMarkingFlow(viewer, shape, (result) => {
                        if (!result || !result.feature) {
                          setRoadMarkingMode(false);
                          showToast("Road marking canceled.", Type.NOTIFICATION.INFO);
                          return;
                        }

                        addFeatureToActiveDataSource(result.feature);
                        setRoadMarkingMode(false);
                        showToast("Road marking added successfully.", Type.NOTIFICATION.SUCCESS);
                      });

                      setRoadMarkingHandler(handler);
                      setRoadMarkingMode(true);
                    }}
                  />
                ))}
              </SimpleGrid>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default RoadMarking;
